import React, { useState } from 'react';
import { Button } from './ui/button';

const EnhancedButton = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  loading = false,
  success = false,
  error = false,
  icon = null,
  onClick,
  className = '',
  disabled = false,
  ...props 
}) => {
  const [isClicked, setIsClicked] = useState(false);

  const handleClick = (e) => {
    setIsClicked(true);
    setTimeout(() => setIsClicked(false), 300);
    if (onClick) onClick(e);
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'primary':
        return 'bg-gradient-to-r from-rose-500 via-pink-500 to-rose-600 hover:from-rose-600 hover:via-pink-600 hover:to-rose-700 text-white shadow-lg hover:shadow-rose-500/25';
      case 'secondary':
        return 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white shadow-lg';
      case 'success':
        return 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-emerald-500/25';
      case 'outline':
        return 'border-2 border-rose-400 text-rose-400 hover:bg-rose-400 hover:text-white bg-transparent';
      case 'ghost':
        return 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 bg-transparent';
      default:
        return 'bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white shadow-lg';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-4 py-2 text-sm';
      case 'lg':
        return 'px-8 py-4 text-lg';
      case 'xl':
        return 'px-12 py-6 text-xl';
      default:
        return 'px-6 py-3 text-base';
    }
  };

  const buttonClasses = `
    ${getVariantClasses()}
    ${getSizeClasses()}
    font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 active:scale-95
    ${isClicked ? 'animate-pulse' : ''}
    ${loading ? 'cursor-not-allowed opacity-75' : ''}
    ${success ? 'animate-bounce' : ''}
    ${error ? 'animate-shake' : ''}
    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    relative overflow-hidden ripple
    ${className}
  `;

  return (
    <Button
      onClick={handleClick}
      disabled={disabled || loading}
      className={buttonClasses}
      {...props}
    >
      <div className="flex items-center justify-center space-x-2">
        {loading && (
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
        )}
        {success && !loading && (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )}
        {error && !loading && !success && (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        )}
        {icon && !loading && !success && !error && icon}
        {children && <span>{children}</span>}
      </div>
    </Button>
  );
};

export default EnhancedButton;